import { useEffect, useState } from "react";
import { Download, Loader2, User } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatInt } from "../lib/format";
import { PageHeader } from "../components/common/PageHeader";
import { ProjectScopeBanner } from "../components/common/ProjectScopeBanner";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (m) => { const [y, mm] = String(m).split("-"); return mm ? `${MON[+mm - 1]}-${y}` : m; };
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function MatTable({ rows, totals }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-left text-sm">
        <thead><tr className="border-b border-slate-200 bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
          <th className="px-3 py-2 font-semibold">Material Type</th>
          <th className="px-3 py-2 text-right font-semibold">Issued</th>
          <th className="px-3 py-2 text-right font-semibold">Completed</th>
          <th className="px-3 py-2 text-right font-semibold">Pending</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">No material activity</td></tr>
          ) : rows.map((r) => (
            <tr key={r.type_name} className="border-b border-slate-100">
              <td className="px-3 py-2 text-slate-800">{r.type_name}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatInt(r.issued)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatInt(r.completed)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-amber-700">{formatInt(r.pending)}</td>
            </tr>
          ))}
          <tr className="bg-slate-50 font-semibold">
            <td className="px-3 py-2">Total</td>
            <td className="px-3 py-2 text-right tabular-nums">{formatInt(totals.issued)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatInt(totals.completed)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-amber-700">{formatInt(totals.pending)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function PerformanceReport() {
  const { user } = useAuth();
  const ownOnly = !!user?.is_field_user && !user?.is_admin;
  const [people, setPeople] = useState([]);
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mFrom, setMFrom] = useState("");
  const [mTo, setMTo] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const bulkExport = async () => {
    setBulkBusy(true);
    try {
      const r = await api.get("/reports/performance/bulk", { params: { month_from: mFrom, month_to: mTo }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "performance_all_people.xlsx"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Bulk export failed"); } finally { setBulkBusy(false); }
  };

  useEffect(() => {
    api.get("/reports/performance/people").then((r) => {
      const p = r.data.people || [];
      setPeople(p);
      if (ownOnly && p.length) setCode(p[0].code);
    }).catch(() => {});
  }, [ownOnly]);

  useEffect(() => {
    if (!code) { setData(null); return; }
    setLoading(true);
    api.get("/reports/performance/summary", { params: { code, month_from: mFrom, month_to: mTo } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [code, mFrom, mTo]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await api.get("/reports/performance/pdf", { params: { code, month_from: mFrom, month_to: mTo }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `performance_${code}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("PDF export failed"); } finally { setBusy(false); }
  };

  const opts = people.map((p) => ({ value: p.code, label: `${p.code} — ${p.name}` }));
  const person = data?.person;

  return (
    <div>
      <PageHeader title="Member / User Performance" subtitle={ownOnly ? "Your complete month-wise performance report" : "Complete month-wise performance for a person (Member Code = User Code)"} />
      <ProjectScopeBanner />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        {ownOnly ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700" data-testid="perf-own-label">
            {data?.person?.user_name || data?.person?.member_name || people[0]?.name || "Your report"}
          </div>
        ) : (
          <div className="w-72"><SearchableSelect testid="perf-person" placeholder="Choose a person by code / name…" value={code} options={opts} onChange={setCode} /></div>
        )}
        <div className="flex items-center gap-1.5 text-sm text-slate-500">From <Input data-testid="perf-from" type="month" value={mFrom} onChange={(e) => setMFrom(e.target.value)} className="h-9 w-40" /> To <Input data-testid="perf-to" type="month" value={mTo} onChange={(e) => setMTo(e.target.value)} className="h-9 w-40" /></div>
        {(mFrom || mTo) && <Button variant="ghost" size="sm" data-testid="perf-clear-range" onClick={() => { setMFrom(""); setMTo(""); }}>Clear</Button>}
        {!ownOnly && (
          <Button data-testid="perf-bulk" size="sm" variant="outline" onClick={bulkExport} disabled={bulkBusy}>
            {bulkBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Bulk Export (All People)
          </Button>
        )}
        {data && (
          <Button data-testid="perf-pdf" size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={downloadPdf} disabled={busy}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Export PDF
          </Button>
        )}
      </div>

      {!code ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center text-sm text-slate-400">Choose a person to view their performance report</div>
      ) : loading ? (
        <div className="grid place-items-center rounded-xl border border-slate-200 bg-white py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : !data ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">No data</div>
      ) : (
        <div className="space-y-6" data-testid="perf-report">
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="grid h-11 w-11 place-items-center rounded-full bg-[var(--mf-purple-600)] text-white"><User className="h-5 w-5" /></div>
            <div>
              <div className="font-display text-lg font-semibold text-slate-900">{person.user_name || person.member_name || person.code}</div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="font-mono">Code: {person.code}</span>
                {person.is_user && <Badge variant="secondary">Field User</Badge>}
                {person.is_member && <Badge variant="secondary">Member</Badge>}
                {!person.is_user && !person.is_member && <span className="text-red-500">No user/member matches this code</span>}
              </div>
            </div>
          </div>

          <div>
            <h2 className="mb-2 font-display text-base font-semibold text-slate-900">Overall Summary</h2>
            <MatTable rows={data.overall.materials} totals={data.overall.material_totals} />
            <div className="mt-2 text-sm text-slate-600" data-testid="perf-overall-rc">RC Work — Completed <span className="font-semibold text-emerald-700">{formatInt(data.overall.rc.completed)}</span> · Draft {formatInt(data.overall.rc.draft)} · Total <span className="font-semibold">{formatInt(data.overall.rc.total)}</span>{data.overall.rc.amount != null && <> · Value <span className="font-semibold text-[var(--mf-purple-600)]">{money(data.overall.rc.amount)}</span></>}</div>
          </div>

          <div>
            <h2 className="mb-2 font-display text-base font-semibold text-slate-900">Month-wise Breakdown</h2>
            {data.months.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">No monthly activity</div>
            ) : (
              <div className="space-y-4">
                {data.months.map((m) => (
                  <div key={m.month} data-testid={`perf-month-${m.month}`} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="font-semibold text-slate-800">{monthLabel(m.month)}</span>
                      <span className="text-xs text-slate-500">RC: {formatInt(m.rc.completed)} completed / {formatInt(m.rc.total)} total{m.rc.amount != null && ` · ${money(m.rc.amount)}`}</span>
                    </div>
                    <MatTable rows={m.materials} totals={m.material_totals} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
